import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

// API
import { getMakes } from "@/Common/api/vehicles";

// Helpers
import { and } from "@/Common/helpers/sonar";
import { getSections, getSectionsAvailable } from "@PayPlans/helpers/sections";
import { getMappedOEMFranchises, getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";

// Hooks
import { useLocationFS } from "@/Common/hooks/LocationFS";
import { getExtendedBranchesFranchises, getOEMFranchisePrograms } from "@/Common/hooks/LocationPrograms";

// Defaults builders
import {
	CallActivityPerformanceDefault,
	CustomerSatisfactionDefault,
	PinnacleDefault,
	VolumeBonusDefault,
	RankingBonusDefault,
	ExcellenceMakesDefault,
	LongevityDefault,
	MarketShareDefault,
	GrowthBonusDefault,
	OperatingCostsDefault,
	BuildVariableOperationsGpsBonusSetup,
} from "@PayPlans/helpers/DefaultSections";

// Contexts
import { usePayPlan } from "@PayPlans/context/PayPlans";

// Components
import PayPlansLayout from "@/PayPlans/Layouts/PayPlansLayout";
import SectionContainer from "@Layouts/components/SectionContainer";

// Custom Sections
import {
	CallActivityPerformance,
	CrossStoreCommissions,
	CustomerSatisfaction,
	DefaultCommissions,
	Longevity,
	Pinnacle,
	RankingBonus,
	SFEBonus,
	MarketShare,
	GrowthBonus,
	VolumeBonus,
	OperatingCosts,
	VariableOperationsGpsBonus,
} from "./sections";

// MUI
import { Collapse, Grid } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import PdfPayPlanLayer from "@/PayPlans/views/Layers/Html/Sales/Associates";

const allSections = getSections([
	"volumeBonus",
	"rankingBonus",
	"callActivityPerformance",
	"growthBonus",
	"marketShare",
	"customerSatisfaction",
	"sfe",
	"pinnacle",
	"longevity",
	"operatingCosts",
	"variableOperationsGpsBonus",
]);

// Used to build franchises dependant sections
const excludesMakes = ["Cadillac"];
const checkPrograms = ["sfe", "pinnacle"];

// Constants
const LAZY_TIME = 500;
const contentAlignment = "flex-start";

const OPTIONAL_SECTIONS = new Map([
	["callActivityPerformance", CallActivityPerformanceDefault],
	["longevity", LongevityDefault],
	["customerSatisfaction", CustomerSatisfactionDefault],
	["pinnacle", PinnacleDefault],
	["volumeBonus", VolumeBonusDefault],
	["rankingBonus", RankingBonusDefault],
	["sfe", ExcellenceMakesDefault],
	["marketShare", MarketShareDefault],
	["growthBonus", GrowthBonusDefault],
	["operatingCosts", OperatingCostsDefault],
	["variableOperationsGpsBonus", BuildVariableOperationsGpsBonusSetup],
]);

const isSpecialUpdateSection = (section) => ["customerSatisfaction"].includes(section);
const getSectionsToRemove = (sections = []) => checkPrograms.filter((program) => !sections?.find((item) => item?.program === program));
const getIsSectionButtonDisabled = ({ isValid = false, isReadOnlyReason = false, isSomeNewPayPlan = false, loading = false }) =>
	!isValid || isReadOnlyReason || isSomeNewPayPlan || loading;

export default function PayPlansModel(props) {
	const { data = null, location = null, isNew = false, loading = false, readOnly = false, updateModel = () => {} } = props;

	// Hooks
	const { isSomeNewPayPlan, isValid, selected, useSectionsErrors, position } = usePayPlan();
	const { errors, clearErrors, clearSectionErrors } = useSectionsErrors();
	const { data: locationFS, isLoading: loadingFS } = useLocationFS({ locationId: location.locationId });

	const locationOEMs = getSanitatedLocationOEMs(location?.oems);
	const isReadOnly = !loading && (Boolean(data?.available) || position !== data?.position);

	// States
	const [isLazyLoading, setIsLazyLoading] = useState(true);
	const [sections, setSections] = useState(new Map());

	const { data: makesList, isLoading: loadingMakes } = useQuery({
		queryKey: ["makes"],
		queryFn: async () => getMakes(),
		staleTime: Infinity,
		enabled: !!selected,
	});

	// Programs usage
	const programsUsage = useMemo(() => {
		const extendedBranchesFranchises = getExtendedBranchesFranchises(location?.branchesOEMs, location?.branchesFranchises);
		return getOEMFranchisePrograms(locationFS?.oems, extendedBranchesFranchises);
	}, [location, locationFS]);

	// Sections to remove
	const removeSections = useMemo(() => getSectionsToRemove(programsUsage), [programsUsage]);

	// Sections to disable
	const disableSections = programsUsage?.filter((item) => item.disabled).map((item) => item.program) || [];

	// Sections controls
	const handleAddSection = (key) => {
		const sectionFn = OPTIONAL_SECTIONS.get(key);
		const isFranchiseDependant = ["volumeBonus", "rankingBonus", "marketShare", "growthBonus", "variableOperationsGpsBonus"].includes(key);
		const isSFE = ["sfe"].includes(key);
		const isCSI = ["customerSatisfaction"].includes(key);
		const isLocationOEMDependant = ["operatingCosts"].includes(key);

		if (isFranchiseDependant || isCSI || isLocationOEMDependant) {
			switch (key) {
				case "customerSatisfaction":
					const mappedOEMBranchesFranchises = getMappedOEMFranchises(
						location?.branchesOEMs ?? [],
						location?.branchesFranchises ?? []
					);
					data[key] = sectionFn(mappedOEMBranchesFranchises ?? [], excludesMakes, { useOnlyStoreMetrics: true });
					break;
				case "operatingCosts":
					data[key] = sectionFn(locationOEMs);
					break;
				case "variableOperationsGpsBonus":
					data[key] = sectionFn({ franchises: location?.franchises });
					break;
				default:
					data[key] = sectionFn(location?.franchises || []);
					break;
			}
		} else if (isSFE) {
			const mappedOEMFranchises = getMappedOEMFranchises(locationOEMs ?? [], location?.franchises ?? []) ?? [];
			data[key] = sectionFn(mappedOEMFranchises);
		} else {
			data[key] = sectionFn();
		}

		setSections(getSectionsAvailable(allSections, data, removeSections));
	};

	const handleDeleteSection = (section) => {
		const remove = allSections.get(section);

		// Remove section
		data[section] = null;
		if (remove?.type === "list") {
			data[section] = [];
		}

		clearSectionErrors(section);
		setSections(getSectionsAvailable(allSections, data, removeSections));
		updateModel({ section, data: data[section] });
	};

	const handleUpdateSection = (section, update = null, value = null, isValidSection = true) => {
		if (isSpecialUpdateSection(section)) {
			if (update === "weighting") {
				data[section][update] = value;
			} else {
				data[section]["makeMetrics"]?.forEach((makeMetric, key) => {
					if (makeMetric.make === update) {
						data[section]["makeMetrics"][key] = value;
					}
				});
			}
		} else {
			data[section] = value;
		}

		// Update model
		Boolean(isValidSection) && updateModel({ section, data: data[section] });
	};

	useMemo(() => {
		setSections(getSectionsAvailable(allSections, data, removeSections));
	}, [data, removeSections]);

	const isDOMLoading = useMemo(() => isLazyLoading || loading || loadingFS, [isLazyLoading, loading, loadingFS]);

	useMemo(() => {
		setIsLazyLoading(true);
		if (selected) {
			setTimeout(() => {
				setIsLazyLoading(false);
			}, LAZY_TIME);
		}
	}, [selected, setIsLazyLoading]);

	useEffect(() => {
		return () => {
			clearErrors();
		};
	}, [clearErrors]);

	const isReadOnlyReason = readOnly || isReadOnly;

	return (
		<PayPlansLayout
			htmlComponent={<PdfPayPlanLayer payPlan={data} selected={selected} loading={loading} />}
			data={data}
			allSections={allSections}
			sections={sections}
			disabledSections={disableSections}
			disabled={getIsSectionButtonDisabled({
				isValid,
				isReadOnlyReason,
				isSomeNewPayPlan,
				loading,
			})}
			isNew={isNew}
			isValid={isValid}
			loading={loading}
			readOnly={isReadOnlyReason}
			onActionAddSection={handleAddSection}
		>
			<TransitionGroup component={Grid} container item xs={12}>
				<Collapse sx={{ width: 1 }}>
					<SectionContainer
						sectionId={"default"}
						title={"Default Commissions"}
						collapsible
						loading={loading}
						readOnly={isReadOnlyReason}
					>
						<DefaultCommissions
							sectionId={"default"}
							content={data?.default ?? null}
							franchises={location?.franchises ?? []}
							oems={locationOEMs ?? []}
							location={location}
							useSectionsErrors={useSectionsErrors}
							loading={loading}
							readOnly={isReadOnlyReason}
							onActionUpdate={handleUpdateSection}
						/>
					</SectionContainer>
				</Collapse>

				<Collapse sx={{ width: 1, marginTop: 2 }}>
					<SectionContainer
						sectionId={"crossStoreCommissions"}
						title={"Cross-Store Commissions"}
						collapsible
						loading={loading}
						readOnly={isReadOnlyReason}
						onDeleteAction={handleDeleteSection}
					>
						<CrossStoreCommissions
							sectionId={"crossStoreCommissions"}
							content={data?.crossStoreCommissions || null}
							brands={{
								newVehicles: makesList,
								preOwned: makesList,
							}}
							useSectionsErrors={useSectionsErrors}
							loading={loading}
							loadingMakes={loadingMakes}
							readOnly={isReadOnlyReason}
							onActionUpdate={handleUpdateSection}
						/>
					</SectionContainer>
				</Collapse>

				{and(Boolean(sections.get("volumeBonus")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"volumeBonus"}
							title={sections.get("volumeBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<VolumeBonus
								sectionId={"volumeBonus"}
								position={position}
								content={data?.volumeBonus || null}
								franchises={location?.franchises || []}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("rankingBonus")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"rankingBonus"}
							title={sections.get("rankingBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<RankingBonus
								sectionId={"rankingBonus"}
								content={data?.rankingBonus || null}
								franchises={location?.franchises || []}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("callActivityPerformance")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"callActivityPerformance"}
							title={sections.get("callActivityPerformance").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<CallActivityPerformance
								sectionId={"callActivityPerformance"}
								performance={data?.callActivityPerformance || null}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
								paySwitchIsEditable={true}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("marketShare")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"marketShare"}
							title={sections.get("marketShare").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<MarketShare
								sectionId={"marketShare"}
								content={data?.marketShare}
								franchises={location?.franchises || []}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("growthBonus")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"growthBonus"}
							title={sections.get("growthBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<GrowthBonus
								sectionId={"growthBonus"}
								content={data?.growthBonus}
								franchises={location?.franchises || []}
								oems={location?.oems || []}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("operatingCosts")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"operatingCosts"}
							title={sections.get("operatingCosts").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<OperatingCosts
								sectionId={"operatingCosts"}
								content={data?.operatingCosts}
								oems={locationOEMs ?? []}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("variableOperationsGpsBonus")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"variableOperationsGpsBonus"}
							title={sections.get("variableOperationsGpsBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={readOnly || isReadOnly}
							onDeleteAction={handleDeleteSection}
						>
							<VariableOperationsGpsBonus
								sectionId={"variableOperationsGpsBonus"}
								position={position}
								content={data?.variableOperationsGpsBonus || null}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={readOnly || isReadOnly}
								franchises={location?.franchises}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}
			</TransitionGroup>

			<Grid container item xs={12} columnSpacing={2} sx={!loading ? { paddingTop: "0px !important" } : null}>
				{!isDOMLoading && (
					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{sections.get("customerSatisfaction") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"customerSatisfaction"}
									title={sections.get("customerSatisfaction").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<CustomerSatisfaction
										sectionId={"customerSatisfaction"}
										content={data?.customerSatisfaction}
										errors={errors?.get("customerSatisfaction")}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
						{sections.get("pinnacle") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"pinnacle"}
									title={sections.get("pinnacle").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<Pinnacle
										sectionId={"pinnacle"}
										content={data?.pinnacle}
										loading={loading}
										readOnly={isReadOnlyReason}
										useSectionsErrors={useSectionsErrors}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>
				)}

				{!isDOMLoading && (
					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{sections.get("sfe") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"sfe"}
									title={sections.get("sfe").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<SFEBonus
										sectionId={"sfe"}
										content={data?.sfe}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("longevity") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"longevity"}
									title={sections.get("longevity").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<Longevity
										content={data?.longevity}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>
				)}
			</Grid>
		</PayPlansLayout>
	);
}
PayPlansModel.propTypes = {
	data: PropTypes.shape({
		available: PropTypes.bool,
		position: PropTypes.string,
		default: PropTypes.object,
		crossStoreCommissions: PropTypes.object,
		callActivityPerformance: PropTypes.object,
		customerSatisfaction: PropTypes.object,
		growthBonus: PropTypes.object,
		longevity: PropTypes.array,
		pinnacle: PropTypes.object,
		marketShare: PropTypes.object,
		operatingCosts: PropTypes.object,
		rankingBonus: PropTypes.object,
		sfe: PropTypes.object,
		volumeBonus: PropTypes.object,
	}),
	location: PropTypes.shape({
		locationId: PropTypes.string,
		oems: PropTypes.array,
		franchises: PropTypes.array,
		branchesFranchises: PropTypes.array,
	}),
	isNew: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	updateModel: PropTypes.func,
};
